import { useState, useEffect, useRef } from 'react';
import { confirmDialog } from 'primereact/confirmdialog';
import { useUploadPreApprovalDocumentMutation, useGetPreApprovalDocumentsByIdQuery, useDeletePreApprovalDocumentMutation, useLazyDownloadDocumentByKeyQuery } from '../../../../../api';
import DocumentDownloadService from '../../../../../utils/documentDownloadService';
import { useLanguage } from '../../../../../contexts/LanguageContext';

export const MODULE_CODES = {
    DEALER:    'PREAPPROVAL',
    WARRANTY:  'PREAPPROVAL_WARRANTY_TEAM'
};

const STATUS = {
    DRAFT:    10,
    WAITING:  20,
    RETURNED: 15,
    ACCEPTED: 30,
    REJECTED: 40
};

const splitDocumentsByModule = (documents = []) => ({
    dealerDocuments:    documents.filter(d => d.moduleCode === MODULE_CODES.DEALER),
    warrantyDocuments:  documents.filter(d => d.moduleCode === MODULE_CODES.WARRANTY)
});

const resolveUploadPermission = (isWarrantyTeam, currentStatus) => {
    if (isWarrantyTeam) {
        return currentStatus === STATUS.WAITING;
    }
    // Bayi
    return currentStatus === STATUS.DRAFT || currentStatus === STATUS.RETURNED;
};

const resolveDeletePermission = (isWarrantyTeam,isAdmin, moduleCode, currentStatus) => {

     if (currentStatus === STATUS.ACCEPTED || currentStatus === STATUS.REJECTED) {
        return false;
    }

     if (isAdmin) {
        if (currentStatus === STATUS.DRAFT || currentStatus === STATUS.RETURNED) {
            return moduleCode === MODULE_CODES.DEALER;
        }
        if (currentStatus === STATUS.WAITING) {
            return moduleCode === MODULE_CODES.WARRANTY;
        }
        return false;
    }

    if (isWarrantyTeam) {
        return moduleCode === MODULE_CODES.WARRANTY && currentStatus === STATUS.WAITING;
    }
    // Bayi
    return moduleCode === MODULE_CODES.DEALER &&
        (currentStatus === STATUS.DRAFT || currentStatus === STATUS.RETURNED);
};


/**
 * Custom hook for handling file operations in PreApprovalFormgt
 * Supports multiple file uploads
 * @param {Object} currentPreApproval - Current pre-approval data
 * @param {boolean} editMode - Whether form is in edit mode
 * @param {boolean} dialogVisible - Whether dialog is visible
 * @param {Object} toast - Toast reference for notifications
 * @returns {Object} File operations state and functions
 */
const useFileOperations = (currentPreApproval, editMode, dialogVisible, toast,  isWarrantyTeam = false,isAdmin = false) => {
    // File upload states - always call hooks in the same order
    const [newFiles, setNewFiles] = useState([]);
    const [isProcessingFiles, setIsProcessingFiles] = useState(false);
    const { th } = useLanguage();

    // Document service ref
    const documentService = useRef(new DocumentDownloadService(toast));

    // API hooks for file operations - always call hooks
    const [uploadDocument] = useUploadPreApprovalDocumentMutation();
    const [deleteDocument] = useDeletePreApprovalDocumentMutation();
    const [triggerDownload] = useLazyDownloadDocumentByKeyQuery();

    // Use RTK Query directly for documents
    const {
        data: rawDocuments = [],
        isLoading: isLoadingDocuments,
        refetch: refetchDocuments
    } = useGetPreApprovalDocumentsByIdQuery(currentPreApproval?.id, {
        skip: !currentPreApproval?.id || !editMode
    });

     const currentStatus = currentPreApproval?.status ?? null;

    const allDocuments       = editMode && Array.isArray(rawDocuments) ? rawDocuments : [];
    const { dealerDocuments, warrantyDocuments } = splitDocumentsByModule(allDocuments);

    const canUpload = editMode
        ? resolveUploadPermission(isWarrantyTeam, currentStatus)
        : true; // create modunda bayi her zaman yükleyebilir


    // Clear files when dialog closes
    useEffect(() => {
        if (!dialogVisible) {
            setNewFiles([]);
            setIsProcessingFiles(false);
        }
    }, [dialogVisible]);

    // Update document service toast reference
    useEffect(() => {
        if (documentService.current.toastRef !== toast) {
            documentService.current.toastRef = toast;
        }
    }, [toast]);



    /**
     * Handle multiple file uploads
     * @returns {Promise<boolean>} Success status
     */
    const handleFileUpload = async () => {
        if (newFiles.length === 0 || !currentPreApproval?.id) return true;

        try {
            setIsProcessingFiles(true);

            toast.current?.show({
                severity: 'info',
                summary: th('preApproval.fileOperations.processing'),
                detail: th('preApproval.fileOperations.uploading').replace('{0}', newFiles.length),
                life: 2000
            });

            let successCount = 0;
            let failCount = 0;

            for (const file of newFiles) {
                try {
                    await uploadDocument({
                        PreapprovalFormId: currentPreApproval.id,
                        file: file,
                        description: 'Ön Onay Dosyası'
                    }).unwrap();
                    successCount++;
                } catch (fileError) {
                    console.error('Dosya yükleme hatası:', file.name, fileError);
                    failCount++;
                }
            }

             toast.current?.show(
            failCount > 0
                ? {
                    severity: 'warn',
                    summary: th('preApproval.fileOperations.partialSuccess'),
                    detail:  th('preApproval.fileOperations.uploadPartialSuccess')
                                .replace('{0}', successCount)
                                .replace('{1}', failCount),
                    life: 4000
                }
                : {
                    severity: 'success',
                    summary: th('preApproval.fileOperations.success'),
                    detail:  th('preApproval.fileOperations.uploadSuccess').replace('{0}', successCount),
                    life:    3000
                }
            );


            setNewFiles([]);
            return failCount === 0;
        } catch (error) {
            console.error('Dosya yükleme hatası:', error);
            toast.current?.show({
                severity: 'error',
                summary: th('preApproval.fileOperations.error'),
                detail: th('preApproval.fileOperations.uploadError'),
                life: 3000
            });
            return false;
        } finally {
            setIsProcessingFiles(false);
        }
    };

    /**
     * Create enhanced handler with file upload support
     * @param {Function} originalHandler - Original handler function
     * @returns {Function} Enhanced handler function
     */
    const createEnhancedHandler = (originalHandler) => {
        if (!editMode) {
            return originalHandler;
        }
        return async () => {
            if (newFiles.length > 0) {
                const fileUploadSuccess = await handleFileUpload();
                if (fileUploadSuccess || newFiles.length === 0) {
                    await originalHandler();
                }
            } else {
                await originalHandler();
            }
        };
    };

    /**
     * Handle file download
     * @param {Object} documentData - Document data
     */
    const handleDownloadDocument = async (documentData) => {
        try {
            await documentService.current.downloadByKey(triggerDownload, documentData.fileKey, documentData, {
                successMessage: 'Dosya başarıyla indirildi.',
                errorMessage: 'Dosya indirilemedi.'
            });
        } catch (error) {
            console.error('Download failed:', error);
        }
    };

    /**
     * Handle existing document deletion
     * @param {Object} documentData - Document to delete
     */
    const handleDeleteExistingDocument = async (documentData) => {
        if (!editMode || !currentPreApproval?.id) return;

          const canDelete = resolveDeletePermission(isWarrantyTeam,isAdmin, documentData.moduleCode, currentStatus);
        if (!canDelete) return;

        confirmDialog({
            message: th('preApproval.fileOperations.deleteConfirm').replace('{0}', documentData.fileName),
            header: th('preApproval.fileOperations.deleteHeader'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            acceptLabel: th('preApproval.fileOperations.deleteAccept'),
            rejectLabel: th('preApproval.fileOperations.deleteReject'),
            accept: async () => {
                try {
                    await deleteDocument({
                        PreapprovalFormId: currentPreApproval.id,
                        DocumentId: documentData.id
                    }).unwrap();

                    toast.current?.show({
                        severity: 'success',
                        summary: th('preApproval.fileOperations.success'),
                        detail: th('preApproval.fileOperations.deleteSuccess'),
                        life: 3000
                    });

                    refetchDocuments();
                } catch (error) {
                    console.error('Dosya silme hatası:', error);
                    toast.current?.show({
                        severity: 'error',
                        summary: th('preApproval.fileOperations.error'),
                        detail: th('preApproval.fileOperations.deleteError'),
                        life: 3000
                    });
                }
            }
        });
    };


const handleDownloadAllAsZip = async (moduleCode = null) => {
    if (!currentPreApproval?.id) return;

    try {
        setIsProcessingFiles(true);

        const baseUrl = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
        let url = `${baseUrl}/v1/preapproval/DownloadAllDocuments?PreApprovalFormId=${currentPreApproval.id}`;
        if (moduleCode) {
            url += `&ModuleCode=${encodeURIComponent(moduleCode)}`;
        }

        let locale = 'TR';
        try {
            const savedLanguage = localStorage.getItem('selectedLanguage');
            if (savedLanguage) {
                const languageData = JSON.parse(savedLanguage);
                const localeMap = { TR: 'TR', US: 'US', DE: 'DE', FR: 'FR', IT: 'IT', RO: 'RO' };
                locale = localeMap[languageData.code] || 'TR';
            }
        } catch (e) {
            // varsayılan TR
        }

        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Accept': 'application/zip, application/octet-stream',
                'Accept-Language': locale
            }
        });

        if (!response.ok) {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                const errorData = await response.json();
                throw new Error(errorData.message || errorData.errorMessage || 'Download failed');
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        if (blob.size === 0) {
            toast.current?.show({
                severity: 'warn',
                summary: th('preApproval.fileOperations.error'),
                detail: th('preApproval.fileOperations.noDocuments'),
                life: 3000
            });
            return;
        }

        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;

        const contentDisposition = response.headers.get('content-disposition');
        let fileName = `PreApproval_${currentPreApproval.id}_Documents.zip`;
        if (contentDisposition) {
            const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
            if (matches != null && matches[1]) {
                fileName = matches[1].replace(/['"]/g, '');
            }
        }

        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        toast.current?.show({
            severity: 'success',
            summary: th('preApproval.fileOperations.success'),
            detail: th('preApproval.fileOperations.downloadSuccess'),
            life: 3000
        });
    } catch (error) {
        console.error('Dosyalar indirilirken hata:', error);
        toast.current?.show({
            severity: 'error',
            summary: th('preApproval.fileOperations.error'),
            detail: th('preApproval.fileOperations.downloadError'),
            life: 3000
        });
    } finally {
        setIsProcessingFiles(false);
    }
};


       return {
        // State
        newFiles,
        setNewFiles,
        dealerDocuments,
        warrantyDocuments,
        isProcessingFiles,
        isLoadingDocuments,
        canUpload,

        // Functions
        handleFileUpload,
        createEnhancedHandler,
        handleDownloadDocument,
        handleDownloadAllAsZip,
        handleDeleteExistingDocument,
        refetchDocuments
    };
};

export default useFileOperations;